import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/store/auth";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/confirm";
import {
  LogOut, BarChart3, ScrollText, Lock, ChevronRight,
  CheckCircle2, AlertTriangle, Clock, User,
} from "lucide-react";

export default function MobileProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/reports/individual")
      .then(r => setStats(r.data?.data?.stats || {}))
      .catch(() => {});
  }, []);

  const doLogout = async () => {
    if (!(await confirmDialog({ title: "Sign out", message: "Sign out of SKIVERSE Task App?", confirmText: "Sign out", danger: true }))) return;
    await logout();
    navigate("/login");
  };

  const changePw = async () => {
    if (pwForm.new_password.length < 8) { toast.error("Password too short"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", pwForm);
      toast.success("Password changed");
      setPwForm({ current_password: "", new_password: "" });
      setShowPw(false);
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const STAT_ITEMS = stats ? [
    { label: "Total", value: stats.total ?? 0, color: "text-slate-900" },
    { label: "Completed", value: stats.completed ?? 0, color: "text-emerald-700", icon: CheckCircle2 },
    { label: "Delayed", value: stats.delayed ?? 0, color: "text-red-600", icon: AlertTriangle },
    { label: "Pending", value: stats.pending ?? 0, color: "text-amber-600", icon: Clock },
  ] : [];

  const MENU = [
    { label: "Reports", icon: BarChart3, action: () => navigate("/m/reports"), color: "text-violet-600" },
    { label: "Audit Logs", icon: ScrollText, action: () => navigate("/m/audit"), color: "text-slate-600" },
    { label: "Change Password", icon: Lock, action: () => setShowPw(v => !v), color: "text-slate-600" },
  ];

  return (
    <div className="px-4 pt-4 pb-8">
      {/* Avatar + info */}
      <div className="flex flex-col items-center py-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#334E7B] to-[#1F3050] flex items-center justify-center text-white text-3xl font-heading font-bold mb-3 shadow-lg">
          {user?.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div className="text-xl font-heading font-bold text-slate-900">{user?.name}</div>
        <div className="text-sm text-slate-500 mt-0.5">{user?.email}</div>
        <div className="mt-2 px-3 py-1 bg-[#334E7B]/10 text-[#334E7B] text-xs font-semibold rounded-full">
          {user?.role_name || user?.role_code}
        </div>
      </div>

      {/* My stats */}
      {stats && (
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">My Task Stats</div>
          <div className="grid grid-cols-4 gap-2">
            {STAT_ITEMS.map(({ label, value, color }) => (
              <div key={label} className="text-center">
                <div className={`text-xl font-heading font-bold ${color}`}>{value}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
          {stats.performance_score !== undefined && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
              <div className="text-xs text-slate-500 font-medium">Performance Score</div>
              <div className="text-lg font-heading font-bold text-emerald-700">
                {stats.performance_score}<span className="text-xs text-slate-400">/100</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Menu items */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden mb-4">
        {MENU.map(({ label, icon: Icon, action, color }, i) => (
          <button key={label} onClick={action}
            className={`w-full flex items-center gap-3 px-4 py-3.5 active:bg-slate-50 transition-colors ${i > 0 ? "border-t border-slate-100" : ""}`}>
            <div className={`w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center ${color}`}>
              <Icon size={16} />
            </div>
            <span className="flex-1 text-sm font-medium text-slate-700 text-left">{label}</span>
            <ChevronRight size={16} className="text-slate-300" />
          </button>
        ))}
      </div>

      {/* Change password form (expand) */}
      {showPw && (
        <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-4 space-y-3">
          <div className="text-sm font-semibold text-slate-800">Change Password</div>
          <input type="password" placeholder="Current password" value={pwForm.current_password}
            onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })}
            className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#334E7B]" />
          <input type="password" placeholder="New password (min 8)" value={pwForm.new_password}
            onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })}
            className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-[#334E7B]" />
          <button onClick={changePw} disabled={saving}
            className="w-full py-2.5 bg-[#334E7B] text-white rounded-xl text-sm font-semibold active:scale-95 transition-transform">
            {saving ? "Saving..." : "Update Password"}
          </button>
        </div>
      )}

      {/* Logout */}
      <button onClick={doLogout}
        className="w-full flex items-center justify-center gap-2 py-3.5 bg-red-50 text-red-600 rounded-2xl font-semibold text-sm border border-red-100 active:scale-95 transition-transform">
        <LogOut size={16} /> Sign Out
      </button>

      <div className="text-center text-[10px] text-slate-300 mt-6">SKIVERSE Task App · v1.0</div>
    </div>
  );
}
